import momentTimezone from 'moment-timezone';
import { ScheduledPayment } from '@dill/dill-shared';

/**
 * Display helpers shared by the scheduled-payment list and its details modal.
 *
 * Kept in their own module so those two can both use them without importing each other — the list
 * renders the modal, so anything the modal needed from the list would be a cycle.
 */

/**
 * Scheduled payments run on a calendar day in this zone and are stored as the start of that day, so
 * they have to be rendered in it — formatting the raw instant in the browser's zone shows the day
 * before for anyone west of PT. Matches AUTOPAY_TIMEZONE on the server.
 */
export const SCHEDULED_PAYMENT_TIMEZONE = 'America/Los_Angeles';

/** The calendar day a scheduled payment runs on, formatted for display. */
export const formatScheduledDate = (scheduledDate: Date | string, format: string): string =>
  momentTimezone(scheduledDate).tz(SCHEDULED_PAYMENT_TIMEZONE).format(format);

/**
 * The same calendar day rebuilt as a local Date, so a picker highlights the day the payment actually
 * runs and formatting it back to `YYYY-MM-DD` round-trips to that same day.
 */
export const toLocalCalendarDay = (scheduledDate: Date | string): Date => {
  const inZone = momentTimezone(scheduledDate).tz(SCHEDULED_PAYMENT_TIMEZONE);
  return new Date(inZone.year(), inZone.month(), inZone.date());
};

/** The supplier response adds the paying customer's name; the buyer side resolves names locally. */
export type ScheduledPaymentWithNames = ScheduledPayment & { buyerName?: string | null };
